import { useCallback } from 'react';
import { useBeforeUnload, useLocation } from 'react-router-dom';
import { Spinner } from '@components/Spinner';
import { TooltipedIcon } from '@components/TooltipedIcon';
import { CONTRACT_BUILDER_HEADER_WRAPPER_Z_INDEX } from '@constants/z-indices';
import { useContractFormPreventableLink } from '@ContractBuilder/hooks';
import { useBlockEditFormStore } from '@ContractBuilder/modules/block-edit';
import { UnsavedChangesModal } from '@ContractBuilder/modules/block-edit/views/UnsavedChangesModal';
import { useDatapointsStore } from '@ContractBuilder/store/datapoints.store';
import { useContractsListingBackPath } from '@features/saved-views';
import { canChange } from '@helpers/canChange';
import { DocumentMode } from '@helpers/document-mode';
import { ArrowLeftIcon } from '@heroicons/react/outline';
import { useDocumentLocation } from '@hooks/useDocumentLocation';
import { useFeatureFlags } from '@hooks/useFeatureFlags';
import { useModalDispatch } from '@hooks/useModalProvider';
import { mdiHistory } from '@mdi/js';
import { MODALS } from '@src/constants';
import { getDocumentMode } from '@src/helpers/getDocumentMode';
import { isEndorsementPath, isPPLFrontend } from '@utils/app-paths';
import clsx from 'clsx';
import dayjs from 'dayjs';

import Nav from '../../../routes';
import { useEntityStore } from '../store';

import { ContractToolsController } from './modules/contract-tools';
import { EditableNameField } from './modules/entity-details/views/EditableNameField';
import { EntityDetails } from './modules/entity-details/views/EntityDetails';
import { ViewSelectionTabs } from './components';
import {
  getEndorsementPublishingOptions,
  getSubmissionPublishingOptions,
  getTemplatePublishingOptions,
} from './constants';

const getConfig = (
  mode: DocumentMode,
  ids: string[],
  hasFirmOrderStatus: boolean,
  hasTemplateReviewWorkflow: boolean,
  hasEndorsementReview: boolean,
  contractsListingPath: string,
) =>
  ({
    [DocumentMode.TEMPLATES]: {
      arrowBack: { to: Nav.Templates, title: 'templates' },
      publishingOptions: getTemplatePublishingOptions(hasTemplateReviewWorkflow),
    },
    [DocumentMode.SUBMISSIONS]: {
      arrowBack: isPPLFrontend ? undefined : { to: contractsListingPath, title: 'contracts' },
      publishingOptions: getSubmissionPublishingOptions(hasFirmOrderStatus),
    },
    [DocumentMode.ENDORSEMENTS]: {
      arrowBack: isPPLFrontend
        ? undefined
        : { to: Nav.SubmissionContract.replace(':id', ids[0]), title: 'original contract' },
      publishingOptions: getEndorsementPublishingOptions(hasEndorsementReview),
    },
    [DocumentMode.OTHER]: {
      publishingOptions: [],
    },
    [DocumentMode.BRANDING]: {
      publishingOptions: [],
    },
  })[mode];

export const ContractBuilderHeader = () => {
  const { pathname } = useLocation();
  const { isTemplate } = useDocumentLocation();
  const isEditing = useBlockEditFormStore((state) => state.formValues?.id !== undefined);
  const { isDirty } = useDatapointsStore(({ isDirty }) => ({ isDirty }));
  const { isBlocked, onDisregardChanges, onLinkClick, onReset, onSaveChanges } = useContractFormPreventableLink();
  const { showModal } = useModalDispatch();
  const { submission, isLoading, updateResourceData } = useEntityStore(
    ({ submission, updateResourceData, isLoading }) => ({
      isLoading,
      submission,
      updateResourceData,
    }),
  );

  const getFeatureFlagState = useFeatureFlags();
  const hasFirmOrderStatus = getFeatureFlagState('FirmOrderStatus');
  const hasTemplateReviewWorkflow = getFeatureFlagState('TemplateReviewWorkflow');
  const hasEndorsementReview = getFeatureFlagState('EndorsementReview');

  const { mode, ids } = getDocumentMode(pathname);
  const contractsListingPath = useContractsListingBackPath();
  const config = getConfig(
    mode,
    ids,
    hasFirmOrderStatus,
    hasTemplateReviewWorkflow,
    hasEndorsementReview,
    contractsListingPath,
  );
  const canNavigate = !(isDirty || isEditing) && !isLoading;
  const isEndorsement = isEndorsementPath();
  const hasOperationsDisabled = !canChange(submission?.status) || isEndorsement;

  const handleSave = useCallback(
    async (name: string, value?: string | number[]) => {
      await updateResourceData({ [name]: value });
    },
    [updateResourceData],
  );

  const handleUnload = useCallback(
    (event: BeforeUnloadEvent) => {
      if (!canNavigate) {
        event.preventDefault();
        return (event.returnValue = 'Are you sure you want to exit?');
      }
    },
    [canNavigate],
  );

  useBeforeUnload(handleUnload);

  if (mode === DocumentMode.OTHER) {
    return null;
  }

  if (!submission) {
    return <Spinner />;
  }

  const dateInfo = `Last updated ${dayjs(submission.updated_at ?? submission.created_at).fromNow()}${
    !!submission.last_updated_by && ` by ${submission.last_updated_by.name}`
  }`;

  const handleUpdateBaseTemplate = async (id: string, onCloseEditMode: () => void) =>
    showModal(MODALS.REPLACE_BASE_TEMPLATE, { onUpdateSuccess: onCloseEditMode, targetResourceId: id });

  const handleArrowBackClick = () => !!config.arrowBack && onLinkClick(config.arrowBack.to);

  return (
    <div className={`${CONTRACT_BUILDER_HEADER_WRAPPER_Z_INDEX} flex flex-col gap-y-1 border-b bg-white`}>
      <div className="flex flex-col px-5 py-3">
        <div className="flex w-full flex-nowrap items-center justify-between">
          {config.arrowBack && (
            <div>
              <a onClick={handleArrowBackClick} className="group">
                <div
                  title={`Back to ${config.arrowBack.title}`}
                  className={'border-info-600 mt-0.25 mr-2 rounded-full border-2 p-1 group-hover:bg-gray-100'}
                >
                  <ArrowLeftIcon className="text-info-600 h-4 w-4" strokeWidth="3" />
                </div>
              </a>
            </div>
          )}
          <div className="mr-2 flex min-w-0 items-center gap-x-4 2xl:flex-nowrap">
            <EditableNameField
              entity={submission}
              hasOperationsDisabled={(isPPLFrontend && !isTemplate) || hasOperationsDisabled}
              onSave={handleSave}
            />
            <TooltipedIcon
              content={dateInfo}
              maxWidth={200}
              path={mdiHistory}
              className="text-info-400 cursor-default 2xl:hidden"
              size={0.7}
            />
            <p className="text-info-400 hidden cursor-default text-sm font-light whitespace-nowrap 2xl:block">
              {dateInfo}
            </p>
          </div>
          <div className="mx-auto">
            <ViewSelectionTabs />
          </div>
          <div id="tools" className="flex shrink-0 flex-row place-items-center gap-5">
            <ContractToolsController publishingOptions={config.publishingOptions} />
          </div>
        </div>
        <EntityDetails
          className={clsx(config.arrowBack ? 'ml-9' : '')}
          entity={submission}
          hasOperationsDisabled={hasOperationsDisabled}
          onSave={handleSave}
          onUpdateBaseTemplate={handleUpdateBaseTemplate}
        />
      </div>
      <UnsavedChangesModal
        isOpen={isBlocked}
        onCancel={onReset}
        onDisregardChanges={onDisregardChanges}
        onSaveChanges={onSaveChanges}
      />
    </div>
  );
};
